import { useCallback } from 'react';
import type { IAiApiLogEntry } from '@skystone/shared';
import { aiApiLogApi } from '@/services/ai-api-log-api';
import { formatDateTime } from '@/lib/date-utils';
import { formatCostUsd } from '@/lib/format-cost';
import { Badge, ErrorBanner, PageLoading } from '@/components/ui';
import { DataTable, type ColumnDef } from '@/components/ui/DataTable';
import { useServerPaginatedQuery } from '@/hooks/useServerPaginatedQuery';

const DEFAULT_PAGE_SIZE = 20;

function formatNumber(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString();
}

function formatLatency(ms: number): string {
  return `${ms.toLocaleString()}ms`;
}

function formatCacheReadWrite(read: number | null, write: number | null): string {
  if (read === null && write === null) return '—';
  return `${read ?? 0} / ${write ?? 0}`;
}

const columns: ColumnDef<IAiApiLogEntry>[] = [
  {
    id: 'createdAt',
    header: 'Timestamp',
    accessor: 'createdAt',
    cell: (val) => formatDateTime(val as string),
  },
  {
    id: 'userId',
    header: 'User',
    accessor: 'userId',
    filter: { type: 'text', placeholder: 'Filter user...' },
  },
  {
    id: 'success',
    header: 'Success',
    accessor: 'success',
    filter: {
      type: 'select',
      options: [
        { label: 'true', value: 'true' },
        { label: 'false', value: 'false' },
      ],
    },
    cell: (val) => (
      <Badge variant={val ? 'success' : 'error'}>{val ? 'true' : 'false'}</Badge>
    ),
  },
  {
    id: 'escalated',
    header: 'Escalated',
    accessor: 'escalated',
    filter: {
      type: 'select',
      options: [
        { label: 'true', value: 'true' },
        { label: 'false', value: 'false' },
      ],
    },
    cell: (val) => (val ? <Badge variant="warning">true</Badge> : <span className="text-muted-foreground">—</span>),
  },
  {
    id: 'latencyMs',
    header: 'Latency',
    accessor: 'latencyMs',
    align: 'right',
    cell: (val) => formatLatency(val as number),
  },
  {
    id: 'model',
    header: 'Model',
    accessor: 'model',
    hideBelow: 'sm',
    filter: {
      type: 'select',
      options: [
        { label: 'gpt-5.4-mini', value: 'gpt-5.4-mini' },
        { label: 'gpt-5.4', value: 'gpt-5.4' },
      ],
    },
    cell: (val) => <span className="text-xs">{val as string}</span>,
  },
  {
    id: 'errorCode',
    header: 'Error',
    accessor: 'errorCode',
    hideBelow: 'sm',
    filter: { type: 'text', placeholder: 'Filter error...' },
    cell: (val) =>
      val ? <Badge variant="error">{val as string}</Badge> : <span className="text-muted-foreground">—</span>,
  },
  {
    id: 'endpoint',
    header: 'Endpoint',
    accessor: 'endpoint',
    hideBelow: 'md',
    filter: {
      type: 'select',
      options: [
        { label: 'insight.metric-view', value: 'insight.metric-view' },
        { label: 'insight.chat', value: 'insight.chat' },
      ],
    },
    cell: (val) => <span className="text-xs">{val as string}</span>,
  },
  {
    id: 'escalationReason',
    header: 'Escalation reason',
    accessor: 'escalationReason',
    hideBelow: 'md',
    cell: (val) =>
      val ? <Badge variant="secondary">{val as string}</Badge> : <span className="text-muted-foreground">—</span>,
  },
  {
    id: 'toolCallCount',
    header: 'Tool calls',
    accessor: 'toolCallCount',
    align: 'right',
    hideBelow: 'lg',
  },
  {
    id: 'inputTokens',
    header: 'Input',
    accessor: 'inputTokens',
    align: 'right',
    hideBelow: 'lg',
    cell: (val) => formatNumber(val as number | null),
  },
  {
    id: 'outputTokens',
    header: 'Output',
    accessor: 'outputTokens',
    align: 'right',
    hideBelow: 'lg',
    cell: (val) => formatNumber(val as number | null),
  },
  {
    id: 'cache',
    header: 'Cache R/W',
    accessor: (row) => formatCacheReadWrite(row.cacheReadTokens, row.cacheWriteTokens),
    align: 'right',
    hideBelow: 'lg',
  },
  {
    id: 'costUsd',
    header: 'Cost (USD)',
    accessor: 'costUsd',
    align: 'right',
    hideBelow: 'md',
    cell: (val) => {
      const formatted = formatCostUsd(val as number | null);
      return formatted ?? <span className="text-muted-foreground">—</span>;
    },
  },
  {
    id: 'scope',
    header: 'Scope',
    accessor: 'scopeJson',
    hideBelow: 'lg',
    cell: (val) => {
      const json = JSON.stringify(val ?? {}, null, 2);
      const compact = JSON.stringify(val ?? {});
      return (
        <span
          className="block max-w-xs truncate text-xs text-muted-foreground"
          title={json}
        >
          {compact}
        </span>
      );
    },
  },
];

export function AiApiLogTab() {
  const fetcher = useCallback(
    ({ page, pageSize, filters }: { page: number; pageSize: number; filters: Record<string, string> }) =>
      aiApiLogApi.getLogs({
        page,
        pageSize,
        userId: filters.userId || undefined,
        success: filters.success || undefined,
        escalated: filters.escalated || undefined,
        model: filters.model || undefined,
        endpoint: filters.endpoint || undefined,
      }),
    [],
  );

  const { data, loading, error, serverPagination, handleFilterChange, showInitialLoading } =
    useServerPaginatedQuery(fetcher, DEFAULT_PAGE_SIZE, 'Failed to load AI API log entries');

  if (showInitialLoading) {
    return <PageLoading />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Logged AI API calls — for cost and usage tracking.
      </p>

      <ErrorBanner message={error} />

      <DataTable
        data={data}
        columns={columns}
        keyExtractor={(entry) => entry.id}
        isLoading={loading}
        emptyMessage="No AI API calls match the current filters."
        pageSizeOptions={[10, 20, 50]}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        enablePagination={false}
        serverPagination={serverPagination}
        enableFilters={true}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
}
